import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus, Search, ChevronLeft, ChevronRight,
  RefreshCw, Building2, Filter, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const VendorList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const { canCreate } = usePermissions('vendors');

  // فلاتر البحث والباجينشن (Server-side)
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorType, setVendorType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showAllSites, setShowAllSites] = useState(false);
  const [searchParams] = useSearchParams();
  const itemsPerPage = 20;
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [filters, setFilters] = useState({
        comp_num: "",
        name: "",
        type: "",
        currency: "",
        org_id: ""
    });

  // 1. دالة جلب البيانات مع دعم الباجينيشن والفلاتر
  const fetchVendors = useCallback(async () => {
  setLoading(true);
  const token = localStorage.getItem('token');
  const skip = (currentPage - 1) * itemsPerPage;

  try {
    const res = await axios.get('/api/companies', {
      params: {
        all_sites: Boolean(showAllSites),
        org_id: sessionOrg,
        skip: skip,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        // 🎯 حقن الفلاتر التفصيلية هنا
        comp_num: filters.comp_num || undefined,
        name: filters.name || undefined,
        type: filters.type || filters.type_filter || undefined, // دعم النوعين
        currency: filters.currency || undefined,
        org_filter: filters.org_id || undefined
      },
      headers: { 'Authorization': `Bearer ${token}` }
    });

    setVendors(res.data.items);
    setTotalPages(res.data.pages);
    setTotalItems(res.data.total);
  } catch (err) {
    toast.error(t('vendors.list.loadError', 'Error loading vendors'));
  } finally {
    setLoading(false);
  }
}, [showAllSites, currentPage, searchTerm, filters, sessionOrg, t]); // 👈 لازم تضيف filters هنا كـ dependency

  // تحديث البيانات عند تغيير الصفحة أو الفلاتر
  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1); // ارجع لأول صفحة عند الفلترة
    };

  const handleRowClick = (compnum) => {
        const queryParams = new URLSearchParams({
            page,
            ...filters,
            all_sites: showAllSites,
            org_id: sessionOrg
        }).toString();
        navigate(`/vendors/${compnum}?${queryParams}`);
    };

  // إعادة ضبط الصفحة عند البحث الجديد
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeChange = (e) => {
    setVendorType(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
        const openAllSites = () => {
            setShowAllSites(true);
            setPage(1);
            setCurrentPage(1);
        };
        const viewMySite = () => {
            setShowAllSites(false);
            setPage(1);
            setCurrentPage(1);
        };

        window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
        window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        return () => {
            window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
            window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        };
    }, []);

  return (
    <div className="flex flex-col h-full bg-surface-app text-[11px] font-sans overflow-hidden" style={{ fontSize: `${fontSize}px` }}>

      {/* --- Toolbar --- */}
      <div className="bg-primary-dark p-3 shadow-lg z-20">
        <div className="flex justify-between items-center px-4">
          <div className="flex items-center gap-6">
            <h2 className="text-white font-black italic uppercase text-lg flex items-center gap-3">
              <Building2 size={24} className="text-primary" />
              <span>{t('vendors.list.title', 'Companies')}</span>
              <span className="bg-primary text-[10px] px-2 py-0.5 rounded-full not-italic ml-2">{totalItems}</span>
              <span className={`text-[9px] px-2 py-0.5 rounded-full uppercase font-bold border not-italic ${showAllSites ? 'bg-status-danger-bg text-status-danger border-status-danger/30' : 'bg-white/10 text-white/80 border-white/20'}`}>
                {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.org', 'Org')}: ${sessionOrg}`}
              </span>
            </h2>

            <div className="relative group">
              <Search className="absolute left-3 top-2.5 text-ink-secondary group-focus-within:text-primary" size={14} />
              <input
                className="pl-9 pr-4 py-2 bg-[#ffffff10] border border-white/10 w-96 outline-none text-white focus:bg-white focus:text-black focus:border-primary shadow-inner rounded-md transition-all placeholder:text-ink-secondary"
                placeholder={t('vendors.list.searchPlaceholder', 'Search by ID, Name or TRN...')}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            <div className="flex items-center gap-2 bg-white/5 p-1 rounded-md border border-white/10">
                <Filter size={12} className="text-ink-secondary ml-2"/>
                <select
                  className="bg-transparent text-ink-secondary border-none p-1 text-[10px] font-bold uppercase outline-none cursor-pointer hover:text-white"
                  value={vendorType}
                  onChange={handleTypeChange}
                >
                  <option value="" className="bg-primary-dark">{t('vendors.list.allTypes', 'All Types')}</option>
                  <option value="VENDOR" className="bg-primary-dark">{t('vendors.fields.vendor', 'Vendor')}</option>
                  <option value="MANUFACTURER" className="bg-primary-dark">{t('vendors.fields.manufacturer', 'Manufacturer')}</option>
                </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={fetchVendors} className="p-2 text-primary hover:bg-white/10 rounded-full transition-all">
               <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            {canCreate && (
              <button onClick={() => navigate('/vendors/new')} className="bg-primary text-white px-6 py-2 rounded-md font-black flex items-center gap-2 uppercase hover:bg-primary-hover shadow-xl active:scale-95 transition-all">
                <Plus size={16} /> {t('vendors.list.new', 'New Company')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- Table Area --- */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border border-line shadow-xl rounded-lg overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-surface-panel border-b text-ink-secondary font-black uppercase text-[9px] sticky top-0 z-10">
              <tr>
                <th className="p-4 border-r w-32 tracking-widest italic">{t('vendors.fields.companyId', 'Company ID')}</th>
                <th className="p-4 border-r tracking-widest italic">{t('vendors.fields.companyName', 'Company Name')}</th>
                <th className="p-4 border-r w-32 tracking-widest italic text-center">{t('vendors.fields.type', 'Type')}</th>
                <th className="p-4 border-r w-24 tracking-widest italic text-center">{t('vendors.fields.currency', 'Currency')}</th>
                <th className="p-4 border-r w-40 tracking-widest italic">{t('vendors.fields.trn', 'TRN')}</th>
                <th className="p-4 tracking-widest italic">{t('common.org', 'Organization')}</th>
              </tr>
              {/* 🎯 Filter Row (نفس شكل الـ WO) */}
              <tr className="bg-surface-panel">
                  <td className="p-1 border-r border-line">
                      <input className="w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card" value={filters.comp_num} onChange={e => handleFilterChange('comp_num', e.target.value)} />
                  </td>
                  <td className="p-1 border-r border-line">
                      <input className="w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card" value={filters.name} onChange={e => handleFilterChange('name', e.target.value)} />
                  </td>
                  <td className="p-1 border-r border-line">
                      <select className="w-full p-1.5 text-primary font-black outline-none border border-line-subtle bg-surface-card" value={filters.type} onChange={e => handleFilterChange('type', e.target.value)}>
                          <option value="" className="bg-primary-dark">{t('vendors.list.allTypes', 'All Types')}</option>
                          <option value="VENDOR" className="bg-primary-dark">{t('vendors.fields.vendor', 'Vendor')}</option>
                          <option value="MANUFACTURER" className="bg-primary-dark">{t('vendors.fields.manufacturer', 'Manufacturer')}</option>
                      </select>
                  </td>
                  <td className="p-1 border-r border-line bg-surface-panel"></td>
                  <td className="p-1 border-r border-line bg-surface-panel"></td>
                  <td className="p-1 border-line">
                      <input className="w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card text-center" value={filters.org_id} onChange={e => handleFilterChange('org_id', e.target.value)} />
                  </td>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {loading ? (
                <tr>
                  <td colSpan="6" className="p-20 text-center">
                    <div className="flex flex-col items-center gap-4 text-ink-secondary italic">
                      <RefreshCw size={40} className="animate-spin text-primary-soft" />
                      {t('vendors.list.loading', 'Loading Company Directory...')}
                    </div>
                  </td>
                </tr>
              ) : vendors.length === 0 ? (
                <tr><td colSpan="6" className="p-20 text-center text-ink-secondary italic">{t('vendors.list.noMatch', 'No companies matching your search.')}</td></tr>
              ) : vendors.map((v) => (
                <tr key={v.comp_id} className="hover:bg-primary-soft cursor-pointer transition-colors group" onClick={() => handleRowClick(v.comp_num)}>
                  <td className="p-4 border-r font-black text-primary uppercase group-hover:text-primary-hover">{v.comp_num}</td>
                  <td className="p-4 border-r font-bold text-ink">{v.name}</td>
                  <td className="p-4 border-r text-center">
                    <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-tighter ${v.type === 'VENDOR' ? 'bg-status-warning-bg text-status-warning' : 'bg-accent-soft text-accent'}`}>
                      {v.type}
                    </span>
                  </td>
                  <td className="p-4 border-r text-center text-ink-secondary font-black">{v.currency}</td>
                  <td className="p-4 border-r text-ink-secondary font-mono">{v.trn || '---'}</td>
                  <td className="p-4 text-ink-secondary font-black italic">{v.org_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Pagination Footer --- */}
      <div className="bg-surface-card border-t p-3 flex justify-between items-center px-8 shadow-inner shrink-0">
        <div className="text-ink-secondary font-bold text-[10px] uppercase italic">
          {t('common.showing', 'Showing')} <span className="text-ink">{(currentPage - 1) * itemsPerPage + 1}</span> {t('common.of', 'to')} <span className="text-ink">{Math.min(currentPage * itemsPerPage, totalItems)}</span> {t('common.of', 'of')} <span className="text-primary">{totalItems}</span> {t('vendors.list.records', 'Records')}
        </div>

        <div className="flex items-center gap-1">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(1)}
            className="p-2 border rounded hover:bg-surface-panel disabled:opacity-30 transition-all text-ink"
          ><ChevronsLeft size={14}/></button>

          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
            className="p-2 border rounded hover:bg-surface-panel disabled:opacity-30 transition-all text-ink"
          ><ChevronLeft size={14}/></button>

          <div className="flex gap-1 mx-2">
             {[...Array(totalPages)].map((_, i) => {
               const p = i + 1;
               // إظهار أول 3 صفحات وآخر 3 صفحات والحالية لعدم زحمة الأرقام
               if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                 return (
                   <button
                     key={p}
                     onClick={() => setCurrentPage(p)}
                     className={`w-8 h-8 rounded-md font-black text-[10px] transition-all border
                     ${currentPage === p ? 'bg-primary text-white border-primary shadow-md' : 'bg-surface-card text-ink-secondary hover:border-primary'}`}
                   >{p}</button>
                 );
               }
               if (p === currentPage - 2 || p === currentPage + 2) return <span key={p} className="px-1 text-ink-secondary">...</span>;
               return null;
             })}
          </div>

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
            className="p-2 border rounded hover:bg-surface-panel disabled:opacity-30 transition-all text-ink"
          ><ChevronRight size={14}/></button>

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
            className="p-2 border rounded hover:bg-surface-panel disabled:opacity-30 transition-all text-ink"
          ><ChevronsRight size={14}/></button>
        </div>
      </div>
    </div>
  );
};

export default VendorList;
